import { getCompanyById } from 'src/services/getCompanyById';
import { baseLoader } from './baseLoader';
import { getCompanyStatuses } from 'src/services/getCompanyStatuses';
import { getActivitiesByCompanyId } from 'src/services/getActivitiesByCompanyId';
import { getObjectionsTypes } from 'src/services/getObjectionsTypes';
import { getCompaniesForAreaSelect } from 'src/services/getCompaniesForAreaSelect';
import {
	ACTION_CODE,
	ACTIONS_SET,
} from 'src/components/ui/IconActions/constants';
import { eventFormObjectionsMapper } from 'src/utils/mappers/eventForm/objection.mapper';
import { eventFormContactsMapper } from 'src/utils/mappers/eventForm/contact.mapper';
import {
	createCompanyByIdMap,
	mapLinkedAreaOptions,
} from 'src/utils/mappers/eventForm/area.mapper';
import {
	collectCompanyIdsFromActivities,
	createActivityMapperContext,
	mapActivitiesToEventCards,
} from 'src/utils/mappers/eventCard/activity.mapper';
import { mapCompanyStatusPlate } from 'src/utils/mappers/companyStatusPlate.mapper';
import { parseFilters } from 'src/hooks/filters/useFilters';
import { toActivityEntityTypeEnum } from 'src/utils/mappers/eventCard/activityFilters.mapper';
import { buildEventStatusSelectProps } from 'src/utils/mappers/companyStatus.mapper';

const GROUP_HIDDEN_ACTIONS = new Set([ACTION_CODE.params, ACTION_CODE.worth]);

const buildActivityFilters = (searchParams) => {
	const filters = parseFilters(searchParams);
	const activityFilters = {};

	if (filters.createdBy?.eq?.length) {
		activityFilters.createdBy = { eq: filters.createdBy.eq };
	}

	if (filters.itemEntityType?.eq?.length) {
		activityFilters.itemEntityType = {
			eq: filters.itemEntityType.eq.map(toActivityEntityTypeEnum),
		};
	}

	return activityFilters;
};

export const getCompanyLoader = async ({ request, params: { companyId } }) => {
	return baseLoader(async () => {
		const searchParams = new URL(request.url).searchParams;
		const activityFilters = buildActivityFilters(searchParams);

		const [
			{ company },
			{ items: statuses },
			{ activities, facets: activityFacets },
			{ items: objectionTypes },
		] = await Promise.all([
			getCompanyById(companyId),
			getCompanyStatuses(),
			getActivitiesByCompanyId(companyId, { filters: activityFilters }),
			getObjectionsTypes(),
		]);

		const referencedCompanyIds = collectCompanyIdsFromActivities(
			activities,
		).filter((id) => id !== String(company.id));

		const companiesForLabels = referencedCompanyIds.length
			? await getCompaniesForAreaSelect({ ids: referencedCompanyIds })
			: [];

		const objections = (company.objections ?? []).filter(
			(obj) => !obj.solved,
		);
		const personalContacts = company.personalContacts ?? [];
		const generalContacts = company.generalNumbers ?? [];

		const companyById = {
			...createCompanyByIdMap(companiesForLabels),
			[String(company.id)]: company,
		};

		const mapperContext = createActivityMapperContext({
			statuses,
			objectionTypes,
			companyById,
			companyId: company.id,
		});

		const actions = (
			company.isGroup
				? ACTIONS_SET.filter(
					(action) => !GROUP_HIDDEN_ACTIONS.has(action.id),
				)
				: ACTIONS_SET
		).map((action) => {
			if (
				company.isGroup ||
				action.id !== ACTION_CODE.connected ||
				!action.lines
			) {
				return action;
			}

			return {
				...action,
				lines: action.lines.filter(
					(line) => line.id !== ACTION_CODE.removeArea,
				),
			};
		});

		const eventFormData = {
			comment: {
				placeholder: 'Комментарий',
				multiline: true,
				minRows: 4,
			},
			status: buildEventStatusSelectProps(statuses),
			actions: { actions },
			submitLabel: 'Создать событие',
			cancelLabel: 'Отменить',
			objectionOptions: eventFormObjectionsMapper(objections),
			contactsOptions: eventFormContactsMapper(
				personalContacts,
				generalContacts,
			),
			params: company.isGroup
				? {}
				: {
					knownFrom: company.params?.knownFrom ?? null,
					emails: (company.params?.email ?? []).filter(Boolean),
					sites: (company.params?.sitesExternal ?? []).filter(Boolean),
				},
			areaOptions: mapLinkedAreaOptions(company),
			worthDefaults:
				!company.isGroup && company.worth
					? {
						value: company.worth.value,
						wedding: company.worth.wedding,
						birthday: company.worth.birthday,
						corporative: company.worth.corporative,
						newYear: company.worth.newYear,
						graduation: company.worth.graduation,
						valueAuto: company.worth.valueAuto,
						weddingAuto: company.worth.weddingAuto,
						birthdayAuto: company.worth.birthdayAuto,
						newYearAuto: company.worth.newYearAuto,
					}
					: {},
			companyId: company.id,
			eventSelections: company.isGroup
				? []
				: (company.eventSelections ?? []),
		};

		const eventCards = mapActivitiesToEventCards(activities, mapperContext);

		return {
			company,
			eventFormData,
			eventCards,
			activities,
			activityMapperContext: mapperContext,
			activityFacets,
			statusPlate: mapCompanyStatusPlate(company, statuses),
		};
	}, `Failed to load company with id ${companyId}`, request);
};
